import type { MouseEvent, ReactNode } from 'react';
import { StatusIndicator } from '@interstate/components/StatusIndicator';
import { Tabs } from '@interstate/components/Tabs';
import { ToggleGroup } from '@interstate/components/ToggleGroup';
import { BuildingOfficeIcon } from '@interstate/components/Icons/BuildingOfficeIcon';
import { UserIcon } from '@interstate/components/Icons/UserIcon';
import { UsersIcon } from '@interstate/components/Icons/UsersIcon';

import { CUSTOMER_TABS, type CustomerSubTabKey, type CustomerTabKey } from '../types';

type ErrorEntityKey = CustomerTabKey | typeof CUSTOMER_TABS.INSURANCE;

interface CustomerCardTabsProps {
    activeTab: CustomerTabKey;
    activeSubTab: CustomerSubTabKey;
    hasCoBuyer: boolean;
    onTabChange: (customer: CustomerTabKey) => void;
    onSubTabChange: (subTab: CustomerSubTabKey) => void;
    /** When set, renders a red dot indicator on tabs that have contract errors. */
    errorEntities?: Partial<Record<ErrorEntityKey, boolean>>;
    /** When true, the buyer tab renders as "Business" with a building icon and a "Business Details" sub-tab. */
    isBusinessBuyer?: boolean;
    /** Hides the Buyer / Co-Buyer switch, leaving only the sub-tabs — an operation edits one participant. */
    hidePrimaryTabs?: boolean;
}

const wrapWithErrorDot = (tabKey: ErrorEntityKey, hasError: boolean, children: ReactNode): ReactNode =>
    hasError ? (
        <StatusIndicator data-testid={`error-badge-${tabKey}`} variant="dot">
            {children}
        </StatusIndicator>
    ) : (
        children
    );

const CustomerCardTabs = ({
    activeTab,
    activeSubTab,
    hasCoBuyer,
    onTabChange,
    onSubTabChange,
    errorEntities,
    isBusinessBuyer = false,
    hidePrimaryTabs = false
}: CustomerCardTabsProps): JSX.Element => {
    const hasBuyerError = errorEntities?.[CUSTOMER_TABS.BUYER] === true;
    const hasCoBuyerError = errorEntities?.[CUSTOMER_TABS.CO_BUYER] === true;
    const hasInsuranceError = errorEntities?.[CUSTOMER_TABS.INSURANCE] === true;
    const BuyerIcon = isBusinessBuyer ? BuildingOfficeIcon : UserIcon;

    const buyerSubTabs = [
        {
            value: CUSTOMER_TABS.BUYER_DETAILS,
            label: (
                <span data-testid={`customer-tabs-sub-tab-${CUSTOMER_TABS.BUYER_DETAILS}`}>
                    {wrapWithErrorDot(CUSTOMER_TABS.BUYER, hasBuyerError, isBusinessBuyer ? 'Business Details' : 'Buyer Details')}
                </span>
            ),
            component: <></>,
            active: false
        },
        {
            value: CUSTOMER_TABS.INSURANCE,
            label: (
                <span data-testid={`customer-tabs-sub-tab-${CUSTOMER_TABS.INSURANCE}`}>
                    {wrapWithErrorDot(CUSTOMER_TABS.INSURANCE, hasInsuranceError, 'Insurance')}
                </span>
            ),
            component: <></>,
            active: false
        }
    ];

    const coBuyerSubTabs = [
        {
            value: CUSTOMER_TABS.CO_BUYER_DETAILS,
            label: (
                <span data-testid={`customer-tabs-sub-tab-${CUSTOMER_TABS.CO_BUYER_DETAILS}`}>
                    {wrapWithErrorDot(CUSTOMER_TABS.CO_BUYER, hasCoBuyerError, 'Co-Buyer Details')}
                </span>
            ),
            component: <></>,
            active: true
        }
    ];
    const effectiveTab: CustomerTabKey = hasCoBuyer ? activeTab : CUSTOMER_TABS.BUYER;

    const handleToggleChange = (_event: MouseEvent, value: string) => {
        if (value === CUSTOMER_TABS.BUYER || value === CUSTOMER_TABS.CO_BUYER) {
            onTabChange(value);
        }
    };

    const handleSubTabActivate = (value: string) => {
        onSubTabChange(value as CustomerSubTabKey);
    };

    const subTabs =
        effectiveTab === CUSTOMER_TABS.CO_BUYER
            ? coBuyerSubTabs
            : buyerSubTabs.map((tab) => ({ ...tab, active: tab.value === activeSubTab }));

    return (
        <div data-testid="customer-tabs">
            {hasCoBuyer && !hidePrimaryTabs && (
                <ToggleGroup
                    data-testid="customer-tabs-toggle"
                    onChange={handleToggleChange}
                    toggleButtonOptions={[
                        {
                            selected: effectiveTab === CUSTOMER_TABS.BUYER,
                            'data-testid': 'customer-tabs-toggle-buyer',
                            'aria-label': 'Buyer',
                            children: wrapWithErrorDot(
                                CUSTOMER_TABS.BUYER,
                                hasBuyerError,
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <BuyerIcon data-testid="customer-tabs-toggle-buyer-icon" aria-hidden="true" />{' '}
                                    {isBusinessBuyer ? 'Business' : 'Buyer'}
                                </span>
                            ),
                            value: CUSTOMER_TABS.BUYER
                        },
                        {
                            selected: effectiveTab === CUSTOMER_TABS.CO_BUYER,
                            'data-testid': 'customer-tabs-toggle-coBuyer',
                            'aria-label': 'Co-Buyer',
                            children: wrapWithErrorDot(
                                CUSTOMER_TABS.CO_BUYER,
                                hasCoBuyerError,
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <UsersIcon aria-hidden="true" /> Co-Buyer
                                </span>
                            ),
                            value: CUSTOMER_TABS.CO_BUYER
                        }
                    ]}
                />
            )}
            <Tabs
                data-testid="customer-tabs-sub-tabs"
                id={`customer-tabs-sub-tabs-${effectiveTab}`}
                onActivate={handleSubTabActivate}
                tabs={subTabs}
            />
        </div>
    );
};

export type { CustomerCardTabsProps };
export default CustomerCardTabs;
